import { useState } from 'react';
import { useTheme } from '../lib/theme-provider';
import { ThemedCard } from './ThemedCard';
import { ThemedButton } from './ThemedButton';
import { Search, X, Eye } from 'lucide-react';

interface Player {
  id: string;
  seatNumber: number;
  nickname: string;
  team: 'good' | 'evil';
}

interface LadyOfLakeModalProps {
  isOpen: boolean;
  players: Player[];
  onClose: () => void;
  onSelect: (targetSeat: number) => void;
}

export default function LadyOfLakeModal({
  isOpen,
  players,
  onClose,
  onSelect,
}: LadyOfLakeModalProps) {
  const { theme } = useTheme();
  const [selectedSeat, setSelectedSeat] = useState<number | null>(null);
  const [revealed, setRevealed] = useState(false);

  if (!isOpen) return null;

  const handleSelect = (seatNumber: number) => {
    setSelectedSeat(seatNumber);
    setRevealed(false);
  };

  const handleConfirm = () => {
    if (selectedSeat === null) return;
    onSelect(selectedSeat);
  };

  const selectedPlayer = players.find((p) => p.seatNumber === selectedSeat);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }}
    >
      <ThemedCard className="w-full max-w-md p-6">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div
            className="flex items-center gap-2"
            style={{ color: '#4ade80' }}
          >
            <Search size={24} />
            <h2
              className="text-xl font-bold"
              style={{ color: '#4ade80' }}
            >
              湖中仙女
            </h2>
          </div>
        </div>

        {/* 说明 */}
        <div
          className="mb-6 p-3 rounded-lg text-center text-sm"
          style={{
            backgroundColor: `${theme.colors['accent-green']}10`,
            border: `1px solid ${theme.colors['accent-green']}30`,
            color: theme.colors['text-secondary'],
          }}
        >
          请选择一名玩家查看其阵营
          <br />
          只能查看未被查看过的玩家
        </div>

        {/* 玩家选择 */}
        {!revealed && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {players.map((player) => (
              <button
                key={player.id}
                onClick={() => handleSelect(player.seatNumber)}
                className="flex flex-col items-center p-3 rounded-xl transition-all"
                style={{
                  backgroundColor:
                    selectedSeat === player.seatNumber
                      ? `${theme.colors['accent-gold']}30`
                      : theme.colors['bg-primary'],
                  border: `2px solid ${
                    selectedSeat === player.seatNumber
                      ? theme.colors['accent-gold']
                      : `${theme.colors['border-secondary']}40`
                  }`,
                }}
              >
                {/* 头像 */}
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold mb-2"
                  style={{
                    backgroundColor: theme.colors['accent-purple'],
                    color: theme.colors['bg-primary'],
                  }}
                >
                  {player.nickname[0]}
                </div>
                {/* 昵称 */}
                <span
                  className="text-xs font-medium truncate w-full text-center"
                  style={{ color: theme.colors['text-primary'] }}
                >
                  {player.nickname}
                </span>
                {/* 座位号 */}
                <span
                  className="text-xs"
                  style={{ color: theme.colors['text-muted'] }}
                >
                  #{player.seatNumber}
                </span>

                {/* 已选择标记 */}
                {selectedSeat === player.seatNumber && (
                  <div
                    className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: theme.colors['accent-gold'],
                      color: theme.colors['bg-primary'],
                    }}
                  >
                    已选择
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* 揭示结果 */}
        {revealed && selectedPlayer && (
          <div
            className="mb-6 p-6 rounded-xl text-center"
            style={{
              backgroundColor:
                selectedPlayer.team === 'good'
                  ? `${theme.colors['accent-green']}20`
                  : `${theme.colors['accent-red']}20`,
              border: `2px solid ${
                selectedPlayer.team === 'good'
                  ? theme.colors['accent-green']
                  : theme.colors['accent-red']
              }`,
            }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl font-bold"
              style={{
                backgroundColor:
                  selectedPlayer.team === 'good'
                    ? theme.colors['accent-green']
                    : theme.colors['accent-red'],
                color: theme.colors['bg-primary'],
              }}
            >
              {selectedPlayer.nickname[0]}
            </div>
            <p
              className="text-lg font-bold mb-2"
              style={{ color: theme.colors['text-primary'] }}
            >
              {selectedPlayer.nickname}
            </p>
            <p
              className="text-xl font-bold"
              style={{
                color:
                  selectedPlayer.team === 'good'
                    ? theme.colors['accent-green']
                    : theme.colors['accent-red'],
              }}
            >
              {selectedPlayer.team === 'good' ? '正义阵营' : '邪恶阵营'}
            </p>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-3">
          <ThemedButton variant="secondary" onClick={onClose} className="flex-1">
            <X size={18} className="mr-1" />
            {revealed ? '关闭' : '取消'}
          </ThemedButton>
          {!revealed && (
            <ThemedButton
              onClick={() => setRevealed(true)}
              disabled={selectedSeat === null}
              className="flex-1"
            >
              <Eye size={18} className="mr-1" />
              查看阵营
            </ThemedButton>
          )}
        </div>
      </ThemedCard>
    </div>
  );
}
